import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/PageHeader";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import { Plus, Trash2, Shield, Pencil, Check } from "lucide-react";

const roleColor = { admin: "bg-primary/15 text-primary", manager: "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))]", cameriere: "bg-secondary text-foreground" };
const MODULE_LABELS = {
  menu: "Menù Digitale", prenotazioni: "Prenotazioni", clienti: "Clienti (CRM)",
  magazzino: "Magazzino", fornitori: "Fornitori", lotti: "Tracciabilità",
  haccp: "HACCP", contabilita: "Contabilità", "import-cassa": "Import Cassa",
  turni: "Turni", staff: "Staff",
};
const ALL_MODULES = Object.keys(MODULE_LABELS);
const DEFAULT_CAMERIERE = ["menu", "prenotazioni", "magazzino", "haccp"];
const empty = { name: "", email: "", password: "", role: "cameriere", permissions: DEFAULT_CAMERIERE };

export default function Staff() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [open, setOpen] = useState(false);

  const load = async () => setItems((await api.get("/users")).data);
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditId(null); setForm(empty); setOpen(true); };
  const openEdit = (u) => {
    setEditId(u.id);
    setForm({ name: u.name, email: u.email, password: "", role: u.role, permissions: u.permissions || [] });
    setOpen(true);
  };

  const save = async () => {
    if (!editId && (!form.name || !form.email || !form.password)) return toast.error("Compila tutti i campi");
    try {
      if (editId) {
        await api.put(`/users/${editId}`, { role: form.role, permissions: form.permissions, password: form.password || undefined });
        toast.success("Permessi aggiornati");
      } else {
        await api.post("/auth/register", form);
        toast.success("Membro dello staff aggiunto");
      }
      setOpen(false); setForm(empty); setEditId(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Eliminare questo account?")) return;
    try { await api.delete(`/users/${id}`); toast.success("Eliminato"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggleModule = (m) => setForm((f) => {
    const set = new Set(f.permissions); set.has(m) ? set.delete(m) : set.add(m);
    return { ...f, permissions: [...set] };
  });

  const isAdmin = user?.role === "admin";

  return (
    <div>
      <PageHeader title="Staff" subtitle="Account, ruoli e permessi di accesso alle sezioni" testid="staff-header">
        <Button onClick={openNew} className="rounded-full" data-testid="add-staff-button">
          <Plus className="w-4 h-4 mr-1" /> Nuovo membro
        </Button>
      </PageHeader>

      <Card className="rounded-xl overflow-hidden">
        <Table data-testid="staff-table">
          <TableHeader><TableRow><TableHead>Nome</TableHead><TableHead>Email</TableHead><TableHead>Ruolo</TableHead><TableHead>Sezioni</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {items.map((u) => (
              <TableRow key={u.id} data-testid={`staff-row-${u.id}`}>
                <TableCell className="font-medium flex items-center gap-2"><Shield className="w-4 h-4 text-muted-foreground" />{u.name}</TableCell>
                <TableCell>{u.email}</TableCell>
                <TableCell><span className={`text-xs px-2 py-0.5 rounded-full capitalize ${roleColor[u.role] || ""}`}>{u.role}</span></TableCell>
                <TableCell className="text-xs text-muted-foreground">{u.role === "admin" ? "Tutte" : `${(u.permissions || []).length} sezioni`}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  {isAdmin && (
                    <Button size="icon" variant="ghost" onClick={() => openEdit(u)} data-testid={`edit-staff-${u.id}`}><Pencil className="w-4 h-4" /></Button>
                  )}
                  {isAdmin && u.id !== user.id && (
                    <Button size="icon" variant="ghost" onClick={() => remove(u.id)} data-testid={`delete-staff-${u.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="staff-dialog" className="max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? "Modifica permessi" : "Nuovo membro dello staff"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {!editId && <>
              <div className="space-y-2"><Label>Nome</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="staff-name-input" /></div>
              <div className="space-y-2"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="staff-email-input" /></div>
            </>}
            <div className="space-y-2"><Label>{editId ? "Nuova password (opzionale)" : "Password"}</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={editId ? "Lascia vuoto per non cambiarla" : ""} data-testid="staff-password-input" /></div>
            <div className="space-y-2"><Label>Ruolo</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v, permissions: v === "cameriere" ? DEFAULT_CAMERIERE : form.permissions })}>
                <SelectTrigger data-testid="staff-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cameriere">Cameriere</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="admin">Admin (accesso totale)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {form.role !== "admin" && (
              <div className="space-y-2">
                <Label>Sezioni visibili e modificabili</Label>
                <div className="flex gap-2 mb-1">
                  <Button type="button" variant="outline" size="sm" className="rounded-full" data-testid="perm-all" onClick={() => setForm({ ...form, permissions: [...ALL_MODULES] })}>Tutte</Button>
                  <Button type="button" variant="ghost" size="sm" className="rounded-full" data-testid="perm-none" onClick={() => setForm({ ...form, permissions: [] })}>Nessuna</Button>
                </div>
                <div className="grid grid-cols-2 gap-1">
                  {ALL_MODULES.map((m) => {
                    const on = form.permissions.includes(m);
                    return (
                      <button key={m} type="button" onClick={() => toggleModule(m)} data-testid={`perm-${m}`}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-left text-sm transition-colors ${on ? "border-primary bg-primary/10" : "border-border hover:bg-secondary/60"}`}>
                        <span className={`w-4 h-4 rounded flex items-center justify-center shrink-0 ${on ? "bg-primary text-primary-foreground" : "border border-muted-foreground/40"}`}>{on && <Check className="w-3 h-3" />}</span>
                        {MODULE_LABELS[m]}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <DialogFooter><Button onClick={save} className="rounded-full" data-testid="save-staff-button">{editId ? "Salva permessi" : "Crea account"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
